import styled from 'styled-components';
import React, { useEffect, useMemo, useReducer } from 'react';

import StepSummary from './StepSummary';
import TimeContainer from './TimeContainer';
import { device } from '@src/common/breakpoints';
import { BackdropModal, Spinner } from '@src/components/reusable';
import reducer, { initialState } from '@src/features/rescheduleReducer';
import Calendar from '@src/components/reusable/Calendar';
import HeaderStepsNavigation from '@src/components/reusable/HeaderStepsNavigation';
import { Product, SellingMode, ScheduleTime } from '@src/core/buyerApiClient';
import { useAppDispatch, useAppSelector, useWidgetsTheme as theme } from '@src/core/hooks';
import {
  processReschedule,
  currentRescheduleSelector,
  saleItemRescheduleOngoingSelector,
  loadingSaleItemRescheduleOptionsSelector,
} from '@src/features/sale';
import { convertDatesToCalendarDates } from '@src/common/calendar';

type Props = {
  product: Product;
  onCancel: () => void;
  onConfirm: () => void;
  reservationId: string;
  scheduleTimeMinutes?: number | null;
};

const RescheduleModal = (props: Props) => {
  const { reservationId, product, onConfirm, onCancel } = props;

  const dispatch = useAppDispatch();
  const actionColor = theme('actionColor');
  const currentRescheduleDates = useAppSelector(currentRescheduleSelector(reservationId));
  const reservationRescheduleOngoing = useAppSelector(saleItemRescheduleOngoingSelector);
  const loadingSaleItemRescheduleOptions = useAppSelector(loadingSaleItemRescheduleOptionsSelector);
  const [rescheduleState, rescheduleDispatch] = useReducer(reducer, initialState);
  const { currentStep, steps, visible, date, time } = rescheduleState;

  const isLastStep = steps[steps.length - 1] === currentStep;
  const isWithDateOnly = product.sellingMode === SellingMode.WithDateOnly;
  const isWithDateAndTime = product.sellingMode === SellingMode.WithDateAndTime;

  const handleConfirmReschedule = () => {
    if (date && (isWithDateOnly || isWithDateAndTime)) {
      const scheduling = currentRescheduleDates![date]!.find(
        (scheduling) =>
          isWithDateOnly ||
          scheduling.availableScheduleTimes?.some((scheduleTime) => scheduleTime.minutes === time?.minutes),
      );
      dispatch(processReschedule(reservationId, scheduling!, time?.minutes ?? null));
    }
  };

  const handleCleanFields = () => rescheduleDispatch({ type: 'RESCHEDULE/RESET_FIELDS' });

  const handleCancelReschedule = () => {
    onCancel();
    handleCleanFields();
  };

  const handleSelectDate = (selectedDate: string) => {
    rescheduleDispatch({
      type: 'RESCHEDULE/SELECT_DATE',
      payload: selectedDate,
    });

    handleNextStep();
  };

  const handleSelectTime = (selectedTime: ScheduleTime) =>
    rescheduleDispatch({
      type: 'RESCHEDULE/SELECT_TIME',
      payload: selectedTime,
    });

  const handleGoBackStep = () => {
    const currentStepIndex = steps.indexOf(currentStep!);
    const mustGoTwoStepsBack = isWithDateOnly && currentStep === 'summary';

    rescheduleDispatch({
      type: 'RESCHEDULE/SET_CURRENT_STEP',
      payload: steps[currentStepIndex - (mustGoTwoStepsBack ? 2 : 1)],
    });
  };

  const handleNextStep = () => {
    if (isLastStep) {
      rescheduleDispatch({
        type: 'RESCHEDULE/SET_CURRENT_STEP',
        payload: undefined,
      });

      handleConfirmReschedule();
      return;
    }

    const currentStepIndex = steps.indexOf(currentStep!);
    const mustGoTwoStepsForward = isWithDateOnly && currentStep === 'date';

    rescheduleDispatch({
      type: 'RESCHEDULE/SET_CURRENT_STEP',
      payload: steps[currentStepIndex + (mustGoTwoStepsForward ? 2 : 1)],
    });
  };

  const scheduleTimes = useMemo(
    () =>
      currentRescheduleDates && date
        ? currentRescheduleDates[date]?.reduce<ScheduleTime[]>((carry, scheduling) => {
            let minutesTimes = scheduling.availableScheduleTimes ?? [];
            minutesTimes = minutesTimes.filter((time) =>
              scheduling.scheduleAvailabilities.find((ast) => ast.minutes === time.minutes && ast.available),
            );

            return [...carry, ...minutesTimes];
          }, [])
        : undefined,
    [date, currentRescheduleDates],
  );

  const convertedRescheduleDates = useMemo(
    () => convertDatesToCalendarDates(currentRescheduleDates),
    [currentRescheduleDates],
  );

  useEffect(() => {
    if (!reservationRescheduleOngoing && !currentStep) {
      onConfirm();
      handleCleanFields();
    }
  }, [reservationRescheduleOngoing]);

  return (
    <BackdropModal visible={visible} closeModal={handleCancelReschedule}>
      <Wrapper>
        <HeaderStepsNavigation title={product.name} stepType='reschedule' currentStep={currentStep} steps={steps} />
        <Container>
          {currentStep === 'date' ? (
            <Calendar
              dates={convertedRescheduleDates}
              pending={Object.keys(currentRescheduleDates).length === 0 && loadingSaleItemRescheduleOptions}
              defaultDateReference={date}
              onDateSelect={handleSelectDate}
              tariffToShowThePrice={null}
            />
          ) : null}
          {!reservationRescheduleOngoing && currentStep !== 'date' ? (
            <StepSummary
              time={time}
              selectedDate={date!}
              isLastStep={isLastStep}
              onNextStep={handleNextStep}
              onPreviousStep={handleGoBackStep}
              isWithDateAndTime={isWithDateAndTime}
            >
              {currentStep === 'time' ? (
                <TimeContainer onSelectTime={handleSelectTime} time={time} scheduleTimes={scheduleTimes} />
              ) : null}
            </StepSummary>
          ) : null}
          <SpinnerWrapper hidden={!reservationRescheduleOngoing}>
            <Spinner visible={reservationRescheduleOngoing && !currentStep} spinnerColor={actionColor} />
          </SpinnerWrapper>
        </Container>
      </Wrapper>
    </BackdropModal>
  );
};

export default RescheduleModal;

const Wrapper = styled.article`
  width: 100%;
  pointer-events: all;
  overflow: hidden;
  max-width: 744px !important;
  margin: 0 auto;
  margin-top: 120px;

  ${device.smallTablet} {
    border-radius: 8px;
  }
`;

const Container = styled.section`
  background-color: white;
  position: relative;
  min-height: min-content;
  width: 100%;
  padding: 24px;
`;

const SpinnerWrapper = styled.div`
  width: 82px;
  height: 82px;
  margin: 0 auto;
`;
